import { Component, OnInit } from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationStart,
  Router,
} from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { take } from 'rxjs';

import { DEFAULT_FEATURE_FLAGS } from '@core/constants/feature-flags';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { selectConfigOpenModal } from '@state/config/config.selectors';
import { selectAuthToken } from '@state/auth/auth.selectors';
import {
  setReferrerCodeAction,
  setEntityIdAction,
  setAuthModal,
} from '@state/auth/auth.actions';
import { fetchFeatureFlagsAction } from '@state/config/config.actions';
import {
  setOrganizationUrlSlugAction,
  getOrganizationByShortNameAction,
} from '@state/organization/organization.actions';
import { selectOrganizationUrlSlug } from '@state/organization/organization.selectors';

import { environment } from '../environments/environment';

const findRouteParam = (
  route: ActivatedRoute,
  param: string
): string | null => {
  while (route) {
    if (route.snapshot.paramMap.has(param)) {
      return route.snapshot.paramMap.get(param);
    }
    route = route.firstChild!;
  }
  return null;
};

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  protected title = 'marketplace-frontend';
  public maintenance = false;

  protected urlOrgSlug: string | null = null;

  protected isDetailsScreen = false;
  protected isHomeScreen = false;

  protected lastOrgSlugLoaded: string | null | undefined;

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private route: ActivatedRoute
  ) {
    if (!environment.production) {
      console.log('Environment: ', environment.name);
    }
  }

  ngOnInit(): void {
    this.handleRefCode();
    this.handleEntityId();
    if (this.maintenance) {
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationStart && event.url !== '/maintenance') {
          this.router.navigate(['/maintenance']);
        }
      });
      return;
    } else {
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationStart && event.url === '/maintenance') {
          this.router.navigate(['/']);
        }
      });
    }
    this.fetchFeatureFlags();

    this.store
      .select(selectConfigOpenModal)
      .pipe(untilDestroyed(this))
      .subscribe((openModal) => {
        if (!!openModal) {
          document.body.classList.add('no-scroll');
        } else {
          document.body.classList.remove('no-scroll');
        }
      });

    /**
     * Organization related
     */
    this.router.events.pipe(untilDestroyed(this)).subscribe((event) => {
      this.isDetailsScreen =
        !!this.router.url.startsWith('/properties/') ||
        (!!this.router.url.includes('/o/') &&
          !!this.router.url.includes('/properties/'));
      this.isHomeScreen =
        ((this.router.url === '/' ||
          this.router.url.startsWith('/page/') ||
          this.router.url.startsWith('/?')) &&
          !this.router.url.startsWith('/maintenance') &&
          !this.router.url.startsWith('/disclosures')) ||
        this.router.url.startsWith('/o/');

      if (event instanceof NavigationEnd) {
        const newSlug = findRouteParam(this.route.root, 'slug');
        // Only update it if user is on the home
        // or property details screen
        if (this.isHomeScreen || this.isDetailsScreen) {
          if (newSlug !== this.urlOrgSlug) {
            this.urlOrgSlug = newSlug;
            this.onSlugChanged(newSlug);
          }
        }
      }
    });

    // Load organization on slug change
    this.store
      .select(selectOrganizationUrlSlug)
      .pipe(untilDestroyed(this))
      .subscribe((slug) => {
        if (slug && slug !== this.lastOrgSlugLoaded) {
          this.store.dispatch(
            getOrganizationByShortNameAction({
              shortName: slug,
            })
          );
        }
        this.lastOrgSlugLoaded = slug;
      });
  }

  private onSlugChanged(slug: string | null) {
    this.store.dispatch(
      setOrganizationUrlSlugAction({
        slug: slug,
      })
    );
    // Now the org is being loaded from the global store change
    // if (slug) {
    //   this.store.dispatch(
    //     getOrganizationByShortNameAction({
    //       shortName: slug,
    //     })
    //   );
    // }
  }

  fetchFeatureFlags(): void {
    this.store.dispatch(
      fetchFeatureFlagsAction({ keys: [...DEFAULT_FEATURE_FLAGS] })
    );
  }

  getUrl(): string {
    return window.location.href;
  }

  handleRefCode() {
    const url = this.getUrl();
    const params = new URLSearchParams(url.split('?')[1]);
    const referrerCode = params.get('refCode');
    if (referrerCode) {
      this.store
        .select(selectAuthToken)
        .pipe(take(1))
        .subscribe((token) => {
          if (!token) {
            this.store.dispatch(setReferrerCodeAction({ referrerCode }));
          }
        });
      this.store.dispatch(setAuthModal({ authModal: 'signup' }));
    }
  }

  handleEntityId() {
    const url = this.getUrl();
    const params = new URLSearchParams(url.split('?')[1]);

    const entityId = params.get('entityId');
    if (entityId) {
      this.store
        .select(selectAuthToken)
        .pipe(take(1))
        .subscribe((token) => {
          if (!token) {
            this.store.dispatch(setEntityIdAction({ entityId }));
          }
        });
    }
  }
}
